import {
    array,
    boolean,
    intersection,
    literal,
    nativeEnum,
    number,
    object,
    SafeParseError,
    string,
    TypeOf,
    ZodErrorMap,
    ZodIssueCode,
} from 'zod';

/* ------------------------------- UserGender ------------------------------- */

export enum UserGender {
    Male = 'male',
    Female = 'female',
    Other = 'other',
}

/* ---------------------- RegistrationUpdateRequestBody --------------------- */

export const RegistrationUpdateRequestBody = object({
    password: string().optional(),
    firstName: string().optional(),
    lastName: string().optional(),
    postCode: string().optional(),
    gender: nativeEnum(UserGender).optional(),
    yearOfBirth: number().optional(),
    marketingOptIn: boolean().optional(),
    pairingCode: string().optional(),
    wantsToPurchase: string().optional(),
    wantsToSubscribe: array(string()).optional(),
    wantsToSubscribeInterval: string().optional(),
});
export type RegistrationUpdateRequestBody = TypeOf<
    typeof RegistrationUpdateRequestBody
>;

/* ---------------------- RegistrationCreateRequestBody --------------------- */

export const RegistrationCreateRequestBody =
    RegistrationUpdateRequestBody.extend({
        email: string().email(),
    });
export type RegistrationCreateRequestBody = TypeOf<
    typeof RegistrationCreateRequestBody
>;

/* ----------------------- RegistrationRequestResponse ---------------------- */

export const RegistrationRequestResponse = object({
    id: string(),
});
export type RegistrationRequestResponse = TypeOf<
    typeof RegistrationRequestResponse
>;

/* --------------------------- VerifyEmailResponse -------------------------- */

export const VerifyEmailResponse = object({
    id: string(),
});
export type VerifyEmailResponse = TypeOf<typeof VerifyEmailResponse>;

/* -------------------------- CompleteRegistration -------------------------- */

export const CompleteRegistration = object({
    device: object({
        id: string(),
        platform: string(),
    }).optional(),
    pairingSuccessful: boolean().optional(),
    refreshToken: string(),
    userId: string(),
    wantsToPurchase: string().optional(),
    wantsToSubscribe: array(string()).optional(),
    wantsToSubscribeInterval: string().optional(),
});
export type CompleteRegistration = TypeOf<typeof CompleteRegistration>;

/* ----------------------------- customErrorMap ----------------------------- */

/**
 * customErrorMap
 *
 * We use the custom error map to provide our own error messages that are slightly more verbose/human friendly in this
 * case. See https://github.com/colinhacks/zod/blob/master/ERROR_HANDLING.md#customizing-errors-with-zoderrormap
 *
 * Use https://github.com/colinhacks/zod/blob/master/src/ZodError.ts as a reference when trying to pinpoint the
 * right case and conditionals.
 *
 * @TODO: In the future we'd want to pass translations in to these error messages - or maybe just return error codes so
 * they can be translated in the app itself with the appropriate dictionary key.
 *
 * @param issue
 * @param ctx
 * @returns
 */
export const customErrorMap: ZodErrorMap = (issue, ctx) => {
    switch (issue.code) {
        case ZodIssueCode.invalid_type: {
            if (issue.received === 'undefined') return { message: 'Required' };

            return { message: ctx.defaultError };
        }
        case ZodIssueCode.too_small:
            if (issue.type === 'string' && issue.minimum > 1)
                return {
                    message: `Minimum ${issue.minimum} characters`,
                };
            if (issue.type === 'string') {
                return { message: 'Required' };
            }
            return { message: ctx.defaultError };
        case ZodIssueCode.invalid_enum_value:
            if (ctx.data === undefined) return { message: 'Required' };
            return { message: ctx.defaultError };
        default:
            return { message: ctx.defaultError };
    }
};

/* ------------------------ getRegistrationFormSchema ----------------------- */

/**
 * getRegistrationFormSchema
 *
 * Creates the formSchema on the fly depending on when is required and what isn't.
 * Fields should be considered optional unless they exist in the object and have a value of true
 *
 * @param requiredFields - Object of field keys and whether they are a required field
 * @returns formSchema
 */
export const getRegistrationFormSchema = ({
    email = false,
    firstName = false,
    gender = false,
    lastName = false,
    marketingOptIn = false,
    password: pw = false,
    postCode = false,
    yearOfBirth = false,
}: Record<string, boolean>) => {
    // The schema is an intersection of the main body and the password specific schema. This is needed as the custom
    // validations (refine https://github.com/colinhacks/zod#refine) are not running if default types fail. But we want the "Passwords do not match"
    // error to show alongside the default type validations.
    //
    // This workaround was identified in the GitHub issue below:

    const mainForm = object({
        email: email
            ? string({ errorMap: customErrorMap }).email()
            : string({ errorMap: customErrorMap }).email().optional(),
        firstName: firstName
            ? string({ errorMap: customErrorMap })
            : string({ errorMap: customErrorMap }).optional(),
        gender: gender
            ? nativeEnum(UserGender, { errorMap: customErrorMap })
            : nativeEnum(UserGender, { errorMap: customErrorMap }).optional(),
        lastName: lastName ? string() : string().optional(),
        marketingOptIn: marketingOptIn
            ? literal(true, { errorMap: customErrorMap })
            : boolean({ errorMap: customErrorMap }).optional(),

        postCode: postCode
            ? string({ errorMap: customErrorMap })
            : string({ errorMap: customErrorMap }).optional(),
        yearOfBirth: yearOfBirth
            ? number({ errorMap: customErrorMap })
            : number({ errorMap: customErrorMap }).optional(),
    });

    const passwordForm = object({
        confirmPassword: pw
            ? string({ errorMap: customErrorMap })
            : string({ errorMap: customErrorMap }).optional(),
        password: pw
            ? string({ errorMap: customErrorMap }).min(12)
            : string({ errorMap: customErrorMap }).min(12).optional(),
    }).refine(({ password, confirmPassword }) => password === confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword'],
    });

    return intersection(mainForm, passwordForm);
};

/* -------------------------------- ZodError -------------------------------- */

/**
 * ZodError
 *
 * Makes the parse error type available to be referenced in the consumer-website
 */
export type ZodError<Input> = SafeParseError<Input>;
